import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ApiError, request } from '../lib/api.js';

type ConfirmError = 'used' | 'expired' | 'invalid' | 'retry';

const KNOWN_LINK_ERRORS = ['used', 'expired', 'invalid'] as const;

// F2: nur ein geparster body.error aus {used, expired, invalid} — Codes, die der Server für
// verifyMagicLink tatsächlich liefert — mappt auf die "Link ist kaputt, fordere einen neuen an"-
// Texte. Alles andere (Netzabbruch ohne ApiError, Status >= 500, ein nginx-502 mit HTML-Body,
// ein unparsbarer/unbekannter Body wie validation_error) ist ein TRANSPORTFEHLER, kein Beleg
// dafür, dass der Token kaputt ist. Würde man das auf "invalid" fallen lassen, würde der Nutzer
// zum Neuanfordern aufgefordert — und genau das entwertet über invalidateOpen einen noch
// gültigen, nur gerade nicht erreichbaren Token. Deshalb eigener 'retry'-Zweig ohne diese
// Aufforderung.
function toConfirmError(err: unknown): ConfirmError {
  if (err instanceof ApiError && err.status < 500) {
    try {
      const body = JSON.parse(err.message) as { error?: string };
      if ((KNOWN_LINK_ERRORS as readonly string[]).includes(body.error ?? '')) {
        return body.error as (typeof KNOWN_LINK_ERRORS)[number];
      }
    } catch {
      // unparsbarer Body -> technischer Fallback unten
    }
  }
  return 'retry';
}

// #18 (M365-SafeLinks): diese Seite postet den Token bewusst NICHT beim Rendern — nur beim
// expliziten Klick. Outlook/Defender SafeLinks ruft eingehende Links per GET vorab ab; ein
// useEffect, der hier von selbst POSTet, hätte dasselbe Problem nur verschoben statt gelöst.
export function LoginConfirmPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // F4: der Token wird EINMAL beim ersten Render aus der URL gelesen und danach im State
  // gehalten — nicht mehr live aus searchParams. Der Effekt unten entfernt den Token gleich
  // danach aus der Adresszeile; würde `token` weiter aus searchParams gelesen, würde der Klick
  // ins Leere laufen, sobald die URL bereinigt ist.
  const [token] = useState(() => searchParams.get('token'));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ConfirmError | null>(null);

  useEffect(() => {
    // F4: Token so früh wie möglich aus der Adresszeile tilgen — noch vor dem Klick auf
    // "bestätigen". Schließt den Referer-/Verlaufs-Weg clientseitig, unabhängig von nginx (Task
    // A4 kümmert sich um die Server-Logs), und nimmt dem Browser-Verlauf (Zurück-Taste,
    // Sitzungswiederherstellung, Edge-Profil-Sync) den Token schon vor dem Auslesen. Das ist
    // kein Auto-POST — hier wird nur die URL angepasst, kein Request gefeuert.
    if (token) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      await request('/v1/auth/magic-link/verify', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      // Voller Reload statt SPA-Navigation: GET /v1/auth/me muss mit dem gerade gesetzten
      // Session-Cookie frisch laufen, nicht mit dem alten (nicht angemeldeten) Auth-State weiter.
      // .replace() statt href=, damit dieser Zwischenschritt keinen eigenen Verlaufseintrag
      // erzeugt (F4) — die "Zurück"-Taste soll nicht auf die Bestätigungsseite zurückführen.
      window.location.replace('/');
    } catch (err) {
      setError(toConfirmError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-page p-8">
      <div className="w-full max-w-sm space-y-4 rounded-card border border-[var(--border)] bg-bg-surface p-6 text-center">
        <h1 className="text-lg font-bold text-text-primary">{t('login.confirm.title')}</h1>
        {token ? (
          <>
            <p className="text-sm text-text-body">{t('login.confirm.hint')}</p>
            <button
              type="button"
              onClick={() => void confirm()}
              disabled={submitting}
              className="w-full rounded-btn bg-brand-accent px-4 py-3 text-white disabled:opacity-60"
            >
              {t('login.confirm.button')}
            </button>
            {error && (
              <p role="alert" className="text-sm text-status-fail">
                {t(`login.confirm.error.${error}`)}
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-text-muted">{t('login.confirm.noToken')}</p>
        )}
      </div>
    </div>
  );
}
